import {Component, EventEmitter, Inject, Input, OnInit, Output} from '@angular/core';
import {NavigationHelperService} from '@application/helper/navigation-helper/navigation-helper.service';
import {PlanningItem} from '@domain/planning/planning-item';
import {PlanningItemType} from '@domain/planning/planning-item-type.enum';
import {PlanningMachine} from '@domain/planning/planning-machine';
import {PlanningMachineRun} from '@domain/planning/planning-machine-run';
import {Subscription} from '@domain/profile/subscription';
import {Authentication, AUTHENTICATION} from '@infrastructure/http/authentication/authentication';
import {AlertDialogResult, AssertionUtils, BaseComponent, DialogBuilderFactoryService, DialogType, moment, TimezoneUtils, TranslateService} from '@vdw/angular-component-library';
import {lowerCase} from 'lodash-es';
import {takeUntil} from 'rxjs/operators';
import {PlanningSidebarData} from '../../planning-sidebar-data.interface';
import {PlanningSidebarType} from '../planning-sidebar-type.enum';

@Component({
  selector: 'app-planning-item-sidebar',
  templateUrl: './planning-item-sidebar.component.html',
  styleUrls: ['./planning-item-sidebar.component.scss']
})
export class PlanningItemSidebarComponent extends BaseComponent implements OnInit {
  @Input() public planningItem: PlanningItem;
  @Input() public machine: PlanningMachine;
  @Output() public savePlanningItem: EventEmitter<PlanningItem> = new EventEmitter<PlanningItem>();
  @Output() public deletePlanningItem: EventEmitter<void> = new EventEmitter<void>();
  @Output() public closePlanningItem: EventEmitter<void> = new EventEmitter<void>();
  @Output() public planningItemChange: EventEmitter<PlanningItem> = new EventEmitter<PlanningItem>();
  @Output() public selectPlanningOrder: EventEmitter<number> = new EventEmitter<number>();

  private currentSubscription: Subscription;

  public constructor(
    @Inject(AUTHENTICATION) private readonly authentication: Authentication,
    private readonly translate: TranslateService,
    private readonly dialogBuilderFactoryService: DialogBuilderFactoryService,
    private readonly navigationHelperService: NavigationHelperService<PlanningSidebarData>
  ) {
    super();
  }

  public ngOnInit(): void {
    this.currentSubscription = this.authentication.getCurrentSubscription();
  }

  public onNavigationHelperDestroy(): void {
    this.navigationHelperService.savePartialState<PlanningSidebarData>({
      sidebarType: PlanningSidebarType.PLANNING_ITEM,
      sidebarPlanningItem: this.planningItem,
      sidebarPlanningMachine: this.machine
    });
  }

  public canModifyPlanningItem(): boolean {
    let result = false;

    if (this.isAddingPlanningItem()) {
      result = true;
    } else {
      if (this.isPlanningItemOfTypeRun()) {
        const planningItem = this.planningItem as PlanningMachineRun;
        result = !planningItem.completed;
      } else {
        result = this.isPlanningItemEndDateAfterCurrentDate();
      }
    }

    return result;
  }

  public delete(): void {
    this.dialogBuilderFactoryService
      .getBuilder()
      .openAlertDialog({
        titleText: this.translate.instant('GENERAL.ACTIONS.DELETE_OBJECT', {object: lowerCase(this.translate.instant(`PLANNING.ADD_ITEM.TYPES.${PlanningItemType[this.planningItem.type]}`))}),
        messageText: this.translate.instant('GENERAL.ACTIONS.CONFIRM_DELETE_DESCRIPTION'),
        type: DialogType.CONFIRM_DELETE
      })
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((result: AlertDialogResult) => {
        if (result === AlertDialogResult.CONFIRM) {
          this.deletePlanningItem.emit();
        }
      });
  }

  public save(planningItem: PlanningItem): void {
    this.savePlanningItem.emit(planningItem);
  }

  public close(): void {
    this.closePlanningItem.emit();
  }

  public onPlanningItemChange(planningItem: PlanningItem): void {
    this.planningItemChange.emit(planningItem);
  }

  public onPlanningOrderSelected(planningOrderId: number): void {
    this.selectPlanningOrder.emit(planningOrderId);
  }

  private isPlanningItemEndDateAfterCurrentDate(): boolean {
    const momentCurrentDateForSubscriptionTimezone: Date = TimezoneUtils.convertToDateWithCurrentOffset(new Date().toISOString(), this.currentSubscription.timeZone.code);

    return moment(this.planningItem.endDate).isAfter(momentCurrentDateForSubscriptionTimezone);
  }

  private isPlanningItemOfTypeRun(): boolean {
    return this.planningItem.type === PlanningItemType.RUN;
  }

  private isAddingPlanningItem(): boolean {
    return AssertionUtils.isNullOrUndefined(this.planningItem.id);
  }
}
